import { motion } from 'framer-motion';
import { useEffect } from 'react';
import {
  BsCameraVideoFill,
  BsFillCameraVideoFill,
  BsFillCameraVideoOffFill,
  BsMicFill,
  BsMicMuteFill,
} from 'react-icons/bs';
import { FaMicrophoneLines } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { useSessionContext } from '../../contexts/SessionProvider';
import AcademiaButton from '../CommonComps/Buttons/AcademiaButton';
import SessionLobbyButton from '../CommonComps/Buttons/SessionLobbyButton';
import SessionDeviceDropdown from './SessionDeviceDropdown';

const SessionPermissionsLobby = () => {
  // integration of context hooks here
  const {
    localStream,
    localStreamRef,
    initiateCallHandler,
    videoDevices,
    setSelectedCamera,
    audioInputDevices,
    setSelectedMic,
    askCameraPermissionsHandler,
    askMicPermissionsHandler,
    toggleUserCameraHandler,
    toggleUserMicHandler,
    callHangUpHandler,
    cameraPermissions,
    micPermissions,
    isCameraLoading,
    isCameraEnabled,
    isMicEnabled,
  } = useSessionContext();

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // setting local stream to local stream ref here to show preview
  useEffect(() => {
    if (localStreamRef.current && isCameraEnabled) {
      localStreamRef.current.srcObject = localStream;
    }
  }, [localStream, localStreamRef, isCameraEnabled]);

  console.log(
    'CAMERA: ',
    videoDevices,
    audioInputDevices,
    cameraPermissions?.state
  );

  // rendering session permission lobby component here
  return (
    <section className='w-[95%] 2xl:w-4/5 flex flex-col lg:flex-row items-center justify-between gap-20 bg-base-dark-gray py-5'>
      <div className='space-y-3'>
        <div className='relative w-full lg:w-[500px] xl:w-[800px] h-[200px] md:h-[340px] lg:h-[280px] xl:h-[440px] rounded-lg overflow-hidden bg-base-black'>
          {!isCameraLoading ? (
            videoDevices.length ? (
              <>
                {isCameraEnabled && cameraPermissions?.state === 'granted' ? (
                  <video
                    ref={localStreamRef}
                    className='absolute top-0 left-0 w-full h-auto object-cover xl:object-contain rounded-lg overflow-hidden'
                    muted
                    autoPlay
                  ></video>
                ) : cameraPermissions?.state === 'denied' ? (
                  <div className='h-full flex items-center justify-center'>
                    <p className='text-base-white font-semibold text-2xl'>
                      Camera permission denied!!
                    </p>
                  </div>
                ) : cameraPermissions?.state === 'prompt' ? (
                  <div className='h-full flex items-center justify-center'>
                    <p className='text-base-white font-semibold text-2xl'>
                      Waiting for camera access...
                    </p>
                  </div>
                ) : (
                  <div className='h-full flex items-center justify-center'>
                    <p className='text-base-white font-semibold text-2xl'>
                      Camera is off...
                    </p>
                  </div>
                )}

                <motion.div
                  initial={{ y: 200 }}
                  animate={{ y: 0 }}
                  transition={{ type: 'spring', stiffness: 100, delay: 1 }}
                  className='absolute flex items-center justify-center w-full gap-5 bottom-5'
                >
                  <SessionLobbyButton
                    title={
                      cameraPermissions?.state === 'granted'
                        ? isCameraEnabled
                          ? 'Mute Video'
                          : 'Share Video'
                        : 'Need Camera Permissions'
                    }
                    onClick={
                      cameraPermissions?.state === 'granted'
                        ? toggleUserCameraHandler
                        : askCameraPermissionsHandler
                    }
                    isPermitted={cameraPermissions?.state === 'granted'}
                    isEnabled={isCameraEnabled}
                  >
                    {isCameraEnabled &&
                    cameraPermissions?.state === 'granted' ? (
                      <BsFillCameraVideoFill className='size-4 md:size-6' />
                    ) : (
                      <BsFillCameraVideoOffFill className='size-4 md:size-6' />
                    )}
                  </SessionLobbyButton>

                  <SessionLobbyButton
                    title={
                      micPermissions?.state === 'granted'
                        ? isMicEnabled
                          ? 'Mute Audio'
                          : 'Share Audio'
                        : 'Need Microphone Permissions'
                    }
                    onClick={
                      micPermissions?.state === 'granted'
                        ? toggleUserMicHandler
                        : askMicPermissionsHandler
                    }
                    isPermitted={micPermissions?.state === 'granted'}
                    isEnabled={isMicEnabled}
                  >
                    {isMicEnabled && micPermissions?.state === 'granted' ? (
                      <BsMicFill className='size-4 md:size-6' />
                    ) : (
                      <BsMicMuteFill className='size-4 md:size-6' />
                    )}
                  </SessionLobbyButton>
                </motion.div>
              </>
            ) : (
              <div className='h-full flex items-center justify-center'>
                <p className='text-base-red font-semibold text-2xl'>
                  No Camera Found!!
                </p>
              </div>
            )
          ) : (
            <div className='h-full flex items-center justify-center'>
              <p className='text-base-white font-semibold text-2xl'>
                Camera Loading...
              </p>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={isMicEnabled ? { opacity: 0 } : { opacity: 1 }}
            className='absolute left-5 bottom-5'
          >
            <BsMicMuteFill className='size-6 text-base-red' />
          </motion.div>
        </div>

        <div className='flex flex-wrap gap-3'>
          <SessionDeviceDropdown
            devices={videoDevices}
            onChangeDevice={(device) => setSelectedCamera(device)}
            icon={<BsCameraVideoFill className='size-5' />}
            isDisabled={
              cameraPermissions?.state !== 'granted' ||
              (cameraPermissions?.state === 'granted' && !videoDevices.length)
            }
            disabledMsg={`${
              !videoDevices.length
                ? 'Camera Not Found'
                : cameraPermissions?.state !== 'granted'
                ? 'Permission Needed'
                : ''
            }`}
          />

          <SessionDeviceDropdown
            devices={audioInputDevices}
            onChangeDevice={(device) => setSelectedMic(device)}
            icon={<FaMicrophoneLines className='size-5' />}
            isDisabled={
              micPermissions?.state !== 'granted' ||
              (micPermissions?.state === 'granted' && !audioInputDevices.length)
            }
            disabledMsg={`${
              !audioInputDevices.length
                ? 'Mic Not Found'
                : micPermissions?.state !== 'granted'
                ? 'Permission Needed'
                : ''
            }`}
          />
        </div>
      </div>

      <div className='flex flex-col gap-5 items-center justify-center flex-1'>
        <div className='text-center space-y-1'>
          <h1 className='text-3xl font-semibold text-base-white'>
            Ready To Join The Session?
          </h1>

          <p className='w-4/5 mx-auto text-sm text-base-white/60'>
            Make your necessary changes here. Click on the Join Now button when
            you are ready.
          </p>
        </div>

        <div className='flex items-center justify-center gap-5'>
          <AcademiaButton
            title={
              micPermissions?.state === 'denied'
                ? 'Please, provide microphone permissions!'
                : undefined
            }
            onClick={initiateCallHandler}
            type='button'
            variant='success'
            afterBgColorClass='after:bg-base-dark-gray'
            isDisabled={micPermissions?.state === 'denied'}
            additionalClassNames='!py-2 w-[200px]'
          >
            Join Now
          </AcademiaButton>

          <AcademiaButton
            onClick={() => {
              navigate('/appointment/list');
              callHangUpHandler();
            }}
            type='button'
            variant='danger'
            afterBgColorClass='after:bg-base-dark-gray'
            additionalClassNames='!py-2 w-[200px]'
          >
            Leave Lobby
          </AcademiaButton>
        </div>
      </div>
    </section>
  );
};

export default SessionPermissionsLobby;
